import { ControlLabel, Stack, Switch } from '@all-manager/design-system-v2';
import { addMinutes, differenceInMinutes, startOfDay } from "date-fns";
import { useState } from "react";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import DateField from "./DateFieldOk";

type Props<TFieldValues extends FieldValues, TTransformedValues extends FieldValues> = {
  form: UseFormReturn<TFieldValues, unknown, TTransformedValues>;
  startDateName: FieldPath<TFieldValues>;
  endDateName: FieldPath<TFieldValues>;
  startDateLabel: string;
  endDateLabel: string;
  isRequiredStartDate?: boolean;
  isRequiredEndDate?: boolean;
  isTimeManaged?: boolean;
  // When set, the all-day toggle is persisted to this form field (true = all-day).
  allDayName?: FieldPath<TFieldValues>;
}

export default function StartEndDatesField<TFieldValues extends FieldValues, TTransformedValues extends FieldValues>(props: Props<TFieldValues, TTransformedValues>) {
  const [isTimeManaged, setIsTimeManaged] = useState<boolean>(() =>
    props.allDayName ? !props.form.getValues(props.allDayName) : (props.isTimeManaged ?? false)
  );

  // Keep same duration between start and end dates when start date is changed
  function handleStartDateChange(date: Date) {
    const startDate = props.form.getValues(props.startDateName);
    const endDate = props.form.getValues(props.endDateName);

    if (endDate) {
      const duration = differenceInMinutes(endDate, startDate);
      props.form.setValue(props.endDateName, addMinutes(date, duration), { shouldValidate: true });
    }
  }

  function handleAllDayChange() {
    const nextIsTimeManaged = !isTimeManaged;
    if (isTimeManaged) {
      // Switching back to all-day: strip the time component to keep clean day boundaries.
      const startDate = props.form.getValues(props.startDateName);
      const endDate = props.form.getValues(props.endDateName);
      if (startDate) {
        props.form.setValue(props.startDateName, startOfDay(startDate), { shouldValidate: true });
      }
      if (endDate) {
        props.form.setValue(props.endDateName, startOfDay(endDate), { shouldValidate: true });
      }
    }
    if (props.allDayName) {
      props.form.setValue(props.allDayName, (!nextIsTimeManaged) as never, { shouldValidate: true });
    }
    setIsTimeManaged(nextIsTimeManaged);
  }

  return (
    <Stack spacing={1}>
      <ControlLabel
        control={<Switch checked={!isTimeManaged} onChange={handleAllDayChange} />}
        label="Journée entière"
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
        <DateField
          label={props.startDateLabel}
          form={props.form}
          name={props.startDateName}
          isTimeManaged={isTimeManaged}
          onChange={handleStartDateChange}
          required={props.isRequiredStartDate}
          fullWidth
        />
        <DateField
          label={props.endDateLabel}
          form={props.form}
          name={props.endDateName}
          isTimeManaged={isTimeManaged}
          required={props.isRequiredEndDate}
          fullWidth
        />
      </Stack>
    </Stack>
  );
}
